import { classifyGenerationFailure, getGenerationErrorDetails } from './generation-error-details.js';

export const FILTERED_IMAGE_MESSAGE = "Sogni's content filter stopped this image.";
// Retry resends each image's original filter setting, so the way forward after
// turning the switch on is a new round, not Retry.
export const MATURE_CONTENT_HINT = 'Turn on Mature Content (18+) in Settings, then generate a new round; Retry keeps the original filter setting.';

/**
 * Convert generation transport and billing errors into actionable UI copy.
 * A filter refusal points at the Mature Content switch only when the caller
 * says the account is signed in with the switch off and may render with the
 * filter off (`canDisableFilter`, false unless proven); the hint never applies
 * to the child-safety decline or to a vendor model's own refusal.
 */
export function getFriendlyGenerationError(errorMessage, {
  isLoggedIn = false, matureContentEnabled = false, canDisableFilter = false
} = {}) {
  if (!errorMessage) return 'An unknown error occurred.';

  const details = getGenerationErrorDetails(errorMessage);
  const message = String(details.limitation || details.message || errorMessage);
  const normalized = message.toLowerCase();
  const kind = classifyGenerationFailure(errorMessage);

  if (kind === 'declined') return message;
  // Only the classifier decides what the filter stopped (it matches the socket's
  // default texts too). A vendor refusal's own text says "NSFW", and the Studio
  // banner sees only text, so no word match may replace a message here.
  if (kind === 'filtered') {
    // The hint only helps an account the server would let render with the filter off.
    const showHint = isLoggedIn && !matureContentEnabled && canDisableFilter === true;
    return showHint ? `${FILTERED_IMAGE_MESSAGE} ${MATURE_CONTENT_HINT}` : FILTERED_IMAGE_MESSAGE;
  }
  if (kind === 'model-retired') return 'This model has been retired from the Supernet. Choose a current model to continue.';
  if (['queue', 'billing-recovery', 'plan-limit', 'trial-limit', 'fair-use', 'entitlement', 'request-cost', 'payment-verification', 'free-limits'].includes(kind)) return message;

  if (normalized.includes('insufficient spark balance')) {
    return message.replace(/^Generation failed:\s*/i, '');
  }
  if (normalized.includes('premium spark') || normalized.includes('4054')) {
    return isLoggedIn
      ? 'This model requires Premium Spark. Unlimited covers eligible Sogni models, while some third-party models are billed separately. Choose another model or add Premium Spark.'
      : 'This model requires Premium Spark. Sign in to use Premium Spark or choose a Free Spark model.';
  }
  if (normalized.includes('free spark') || normalized.includes('restricted')) {
    return isLoggedIn
      ? 'This model is not available with Free Spark. Choose a Free Spark model or add Premium Spark.'
      : 'Free Spark limit reached. Sign in for more generation options.';
  }
  if (normalized.includes('timeout')) {
    return 'Generation timed out. The network may be busy—try again.';
  }
  if (normalized.includes('balance') || normalized.includes('insufficient')) {
    return 'Insufficient balance. Check your account credits.';
  }
  return message;
}
